/**
 * Vague 1 · Phase C.4 · Hook réseau pour l'app agent.
 *
 * `navigator.onLine` est imparfait (parfois `true` alors qu'on n'a aucun
 * lien), mais reste utile pour piloter l'UI (badge) et déclencher les
 * tentatives de sync.
 *
 * Pour la fiabilité réelle on s'appuie sur le résultat d'une vraie requête :
 * `ApiTransportError` côté wrapper api-client = passage en mode offline.
 */
import { useEffect, useState } from "react";

export function useNetworkStatus(): {
  online: boolean;
} {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true,
  );

  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  return { online };
}

/**
 * Petit publisher dédié au flush manuel/auto, pour réinitialiser les
 * compteurs de l'UI sans avoir à passer par React Query partout.
 *
 * Les composants qui affichent un compteur outbox peuvent s'abonner pour
 * se rafraîchir après chaque flush.
 */
type Listener = () => void;
const flushListeners = new Set<Listener>();

export function onOutboxChanged(fn: Listener): () => void {
  flushListeners.add(fn);
  return () => {
    flushListeners.delete(fn);
  };
}

export function notifyOutboxChanged(): void {
  for (const fn of flushListeners) fn();
}
